import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { employeesAPI, attendanceAPI } from '../services/api';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Search,
  UserPlus,
  Mail,
  Phone,
  Building,
  User,
  Briefcase,
  MapPin,
  Fingerprint,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { getCurrentNepalTime } from '../utils/timeUtils';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const Employees = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [shiftOptions, setShiftOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [positionOptions, setPositionOptions] = useState([]);
  const [formData, setFormData] = useState({
    employeeId: '',
    name: '',
    email: '',
    password: '',
    position: '',
    department: '',
    phoneNumber: '',
    address: '',
    role: 'employee',
    isActive: true,
    allowMobileManualPunch: false,
    biometricId: '',
    shift: '',
    shiftStart: '',
    lateGraceMinutes: '',
    hourlyRate: '',
    dateOfJoining: format(getCurrentNepalTime(), 'yyyy-MM-dd'),
    emergencyContact: '',
    emergencyPhone: ''
  });

  const roles = ['employee', 'supervisor', 'manager', 'hr', 'admin'];

  useEffect(() => {
    fetchEmployees();
    Promise.all([
      attendanceAPI.listShifts().then(res => setShiftOptions(res.data || [])).catch(() => { }),
      employeesAPI.listDepartments().then(res => setDepartmentOptions(res.data || [])).catch(() => { }),
      employeesAPI.listPositions().then(res => setPositionOptions(res.data || [])).catch(() => { })
    ]);
  }, []);

  const fetchEmployees = async () => {
    try {
      setIsLoading(true);
      const response = await employeesAPI.getAll();
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setIsLoading(false);
    }
  };

  const filterEmployees = useCallback(() => {
    let filtered = employees;

    if (searchTerm) {
      filtered = filtered.filter(emp =>
        emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employeeId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterDepartment) {
      filtered = filtered.filter(emp => emp.department === filterDepartment);
    }

    setFilteredEmployees(filtered);
  }, [employees, searchTerm, filterDepartment]);

  useEffect(() => {
    filterEmployees();
  }, [filterEmployees]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = name === 'hourlyRate'
      ? (value === '' ? '' : Number(value))
      : (type === 'checkbox' ? checked : value);
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      name: '',
      email: '',
      password: '',
      position: '',
      department: '',
      phoneNumber: '',
      address: '',
      role: 'employee',
      isActive: true,
      allowMobileManualPunch: false,
      biometricId: '',
      shift: '',
      shiftStart: '',
      lateGraceMinutes: '',
      hourlyRate: '',
      dateOfJoining: format(getCurrentNepalTime(), 'yyyy-MM-dd'),
      emergencyContact: '',
      emergencyPhone: ''
    });
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      const { employeeId, ...employeeData } = formData;
      await employeesAPI.create(employeeData);
      toast.success('Employee added successfully');
      setShowAddModal(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add employee');
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      await employeesAPI.update(selectedEmployee._id, updateData);
      toast.success('Employee updated successfully');
      setShowEditModal(false);
      setSelectedEmployee(null);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update employee');
    }
  };

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (window.confirm(`Are you sure you want to delete ${employeeName}?`)) {
      try {
        await employeesAPI.delete(employeeId);
        toast.success('Employee deleted successfully');
        fetchEmployees();
      } catch (error) {
        toast.error('Failed to delete employee');
      }
    }
  };

  const openEditModal = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      employeeId: employee.employeeId,
      name: employee.name,
      email: employee.email,
      password: '',
      position: employee.position,
      department: employee.department,
      phoneNumber: employee.phoneNumber || '',
      address: employee.address || '',
      role: employee.role,
      isActive: employee.isActive,
      allowMobileManualPunch: !!employee.allowMobileManualPunch,
      biometricId: employee.biometricId || '',
      shift: employee.shift || '',
      shiftStart: employee.shiftStart || '',
      lateGraceMinutes: employee.lateGraceMinutes ?? '',
      hourlyRate: employee.hourlyRate ?? '',
      dateOfJoining: employee.dateOfJoining ? format(new Date(employee.dateOfJoining), 'yyyy-MM-dd') : format(getCurrentNepalTime(), 'yyyy-MM-dd'),
      emergencyContact: employee.emergencyContact || '',
      emergencyPhone: employee.emergencyPhone || ''
    });
    setShowEditModal(true);
  };

  if (!['admin', 'supervisor', 'manager', 'hr'].includes(user?.role)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-8 bg-slate-50 rounded-lg border border-slate-200">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
            <AlertCircle size={32} />
          </div>
          <h1 className="text-xl font-medium text-gray-900">Access Denied</h1>
          <p className="text-slate-500 mt-2">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-500" />
            Employee Management
          </h1>
          <p className="text-slate-500 mt-1">Manage personnel, roles, and access</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md shadow-none transition-all  "
        >
          <UserPlus className="w-5 h-5 mr-2" />
          Add Employee
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-2 rounded-lg shadow-sm border border-slate-200 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, email or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-transparent border-0 focus:ring-0 text-sm placeholder:text-slate-400"
          />
        </div>
        <div className="h-8 w-px bg-slate-200 hidden sm:block self-center"></div>
        <div className="sm:w-64 px-2">
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="w-full py-2.5 bg-transparent border-0 focus:ring-0 text-sm text-slate-600 font-medium cursor-pointer"
          >
            <option value="">All Departments</option>
            {departmentOptions.map(dept => (
              <option key={dept._id} value={dept.name}>{dept.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Employee Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-200 mb-4"></div>
            <p className="text-slate-500 font-medium">Loading employee data...</p>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No employees found</h3>
            <p className="text-slate-500 mt-1 max-w-sm">Try adjusting your search filters or add a new employee to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100 text-left">
                  <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Employee</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Role & Dept</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Contact</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEmployees.map((employee) => (
                  <tr key={employee._id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-700 font-bold">
                          {employee.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{employee.name}</div>
                          <div className="text-xs text-slate-500 font-mono">{employee.employeeId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                          <Briefcase size={14} className="text-slate-400" />
                          {employee.position}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Building size={14} className="text-slate-400" />
                          {employee.department}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Mail size={14} className="text-slate-400" />
                          {employee.email}
                        </div>
                        {employee.phoneNumber && (
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Phone size={14} className="text-slate-400" />
                            {employee.phoneNumber}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${employee.isActive
                        ? 'bg-gray-100 text-gray-900 border border-gray-200'
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                        }`}>
                        {employee.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(employee)}
                          className="p-2 text-slate-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                          title="Edit Details"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteEmployee(employee._id, employee.name)}
                          className="p-2 text-slate-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                          title="Remove Employee"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Shared Modal Logic for Add/Edit */}
      <AnimatePresence>
        {(showAddModal || (showEditModal && selectedEmployee)) && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
            >
              {/* Modal Header */}
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-md ${showAddModal ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-700'}`}>
                    {showAddModal ? <UserPlus size={20} /> : <Edit size={20} />}
                  </div>
                  <div>
                    <h2 className="text-xl font-medium text-gray-900">{showAddModal ? 'New Employee' : 'Edit Profile'}</h2>
                    <p className="text-sm text-slate-500">{showAddModal ? 'Onboard a new team member' : 'Update employee details'}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="p-2 text-slate-400 hover:bg-slate-200 hover:text-slate-600 rounded-full transition-colors"
                >
                  <Plus size={24} className="rotate-45" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-6 md:p-8">
                <form id="employeeForm" onSubmit={showAddModal ? handleAddEmployee : handleEditEmployee} className="space-y-8">
                  {/* Section: Identity */}
                  <section>
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <User size={14} /> Personal Identity
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {showAddModal && (
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Employee ID</label>
                          <div className="relative">
                            <input type="text" value="Auto-generated" disabled className="w-full pl-4 pr-16 py-2.5 bg-slate-50 border border-slate-200 rounded-md text-slate-400 font-mono text-sm" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">AUTO</span>
                          </div>
                        </div>
                      )}
                      {!showAddModal && (
                        <FormInput label="Employee ID" name="employeeId" value={formData.employeeId} onChange={handleInputChange} required readOnly className="bg-slate-50 font-mono" />
                      )}
                      <FormInput label="Full Name *" name="name" value={formData.name} onChange={handleInputChange} required />
                      <FormInput label="Email Address *" type="email" name="email" value={formData.email} onChange={handleInputChange} required icon={Mail} />
                      <FormInput label={showAddModal ? "Password *" : "New Password"} type="password" name="password" value={formData.password} onChange={handleInputChange} required={showAddModal} placeholder={!showAddModal && "Leave blank to keep current"} />
                      <FormInput label="Phone Number" type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} icon={Phone} />
                      <FormInput label="Biometric ID" name="biometricId" value={formData.biometricId} onChange={handleInputChange} icon={Fingerprint} />
                    </div>
                  </section>

                  {/* Section: Professional */}
                  <section>
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2 border-t border-slate-100 pt-6">
                      <Briefcase size={14} /> Professional Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Department *</label>
                        <select name="department" value={formData.department} onChange={handleInputChange} required className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm transition-all">
                          <option value="">Select Department</option>
                          {departmentOptions.map(d => <option key={d._id} value={d.name}>{d.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Position *</label>
                        <select name="position" value={formData.position} onChange={handleInputChange} required className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm transition-all">
                          <option value="">Select Position</option>
                          {positionOptions.map(p => <option key={p._id} value={p.name}>{p.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">System Role</label>
                        <select name="role" value={formData.role} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm transition-all">
                          {roles.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Shift *</label>
                        <select name="shift" value={formData.shift} onChange={handleInputChange} required className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm transition-all">
                          <option value="">Select Shift</option>
                          {shiftOptions.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
                        </select>
                      </div>
                      <FormInput label="Date of Joining" type="date" name="dateOfJoining" value={formData.dateOfJoining} onChange={handleInputChange} />
                      <FormInput label="Hourly Rate" type="number" name="hourlyRate" value={formData.hourlyRate} onChange={handleInputChange} icon="prefix-$" step="0.01" min="0" />

                      <div className="flex items-center h-full pt-6">
                        <label className="flex items-center cursor-pointer p-4 bg-slate-50 rounded-md border border-slate-200 w-full hover:bg-slate-100 transition-colors">
                          <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleInputChange} className="rounded text-gray-700 focus:ring-gray-200 w-5 h-5 border-slate-300" />
                          <span className="ml-3 font-semibold text-slate-700">Account Active</span>
                        </label>
                      </div>
                      <div className="flex items-center h-full pt-6">
                        <label className="flex items-center cursor-pointer p-4 bg-slate-50 rounded-md border border-slate-200 w-full hover:bg-slate-100 transition-colors">
                          <input
                            type="checkbox"
                            name="allowMobileManualPunch"
                            checked={formData.allowMobileManualPunch}
                            onChange={handleInputChange}
                            className="rounded text-gray-700 focus:ring-gray-200 w-5 h-5 border-slate-300"
                          />
                          <span className="ml-3 font-semibold text-slate-700">Enable Manual Punch</span>
                        </label>
                      </div>
                    </div>
                  </section>

                  {/* Section: Additional */}
                  <section>
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2 border-t border-slate-100 pt-6">
                      <MapPin size={14} /> Contact & Emergency
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Home Address</label>
                        <textarea name="address" value={formData.address} onChange={handleInputChange} rows="2" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm transition-all resize-none" placeholder="Street, City, Country..." />
                      </div>
                      <FormInput label="Emergency Contact Name" name="emergencyContact" value={formData.emergencyContact} onChange={handleInputChange} placeholder="Name of contact person" />
                      <FormInput label="Emergency Phone" type="tel" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleInputChange} icon={Phone} />
                    </div>
                  </section>
                </form>
              </div>

              {/* Modal Footer */}
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  className="px-5 py-2.5 bg-white border border-slate-200 rounded-md text-slate-600 font-semibold hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="employeeForm"
                  className="px-6 py-2.5 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md shadow-none transition-colors flex items-center gap-2"
                >
                  {showAddModal ? <UserPlus size={18} /> : <Edit size={18} />}
                  {showAddModal ? 'Create Employee' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Reusable Form Input
const FormInput = ({ label, type = 'text', name, value, onChange, placeholder, required = false, icon: Icon, readOnly = false, className, step, ...props }) => (
  <div className={className}>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
      {label}
    </label>
    <div className="relative">
      {Icon && typeof Icon !== 'string' && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <Icon size={16} />
        </div>
      )}
      {Icon === 'prefix-$' && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm pointer-events-none">$</div>
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        readOnly={readOnly}
        step={step}
        placeholder={placeholder}
        className={`w-full ${Icon ? 'pl-9' : 'pl-4'} pr-4 py-2.5 bg-white border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm transition-all ${readOnly ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
        {...props}
      />
    </div>
  </div>
);

export default Employees;
